import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Collapsible } from '@openx/components-core/lib/Collapsible/Collapsible.tsx';
import { InfoIcon } from '@phosphor-icons/react/Info';
import { useTranslation } from 'react-i18next';

import { useDealContext } from '../../../utils/context/DealContext.tsx';
import { useGetMappedForecastValues } from '../hooks/useGetMappedForecastValues.ts';

const styles: { [key: string]: SxProps<Theme> } = {
  column: {
    display: 'flex',
    flexDirection: 'column',
  },
  container: { display: 'flex', flexDirection: 'column', gap: theme => theme.spacing(1.5) },
  rowContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme => theme.spacing(1),
    paddingLeft: theme => theme.spacing(2),
  },
  heading: {
    color: theme => theme.palette.text.secondary,
    fontWeight: '600',
  },
  rowWithTooltip: {
    alignItems: 'center',
    display: 'flex',
    gap: theme => theme.spacing(1),
  },
  value: {
    '::before': { display: 'none' },
  },
};

export const DealForecastMetrics = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { useDealForecast } = useDealContext();
  const {
    forecastState: { forecast, audience_monthly_requests, contextual_estimate },
  } = useDealForecast({ shallow: true });
  const {
    requestsWithTargeting,
    audienceReductionFactorLabel,
    contextualImpressionsLabel,
    contextualReductionFactorLabel,
  } = useGetMappedForecastValues({
    audience_monthly_requests,
    contextual_estimate,
    forecast,
  });

  return (
    <Box sx={styles.container}>
      <Box sx={styles.column} data-test="monthly-avails">
        <Typography variant="body2" sx={styles.heading}>
          {t('Monthly Avails')}
        </Typography>
        <Typography sx={styles.value} variant="h2">
          {requestsWithTargeting}
        </Typography>
      </Box>
      {!!audience_monthly_requests && (
        <Box sx={styles.column} data-test="audience-reduction-factor">
          <Box sx={styles.rowWithTooltip}>
            <Typography variant="body2" sx={styles.heading}>
              {t('Audience Reduction Factor')}
            </Typography>
            <Tooltip placement="top" title={t('The percentage of total requests that your audience is included in.')}>
              <InfoIcon
                size={24}
                color={theme.palette.text.secondary}
                data-test="audience-reduction-factor-info-icon"
              />
            </Tooltip>
          </Box>
          <Typography sx={styles.value} variant="h2">
            {audienceReductionFactorLabel}
          </Typography>
        </Box>
      )}
      {!!contextual_estimate && (
        <Box data-test="contextual-estimate-section">
          <Collapsible
            titleRender={
              <Typography variant="h3" data-test="contextual-estimate-title">
                {t('Contextual')}
              </Typography>
            }
          >
            <Box sx={styles.rowContainer}>
              <Box sx={styles.column} data-test="contextual-reduction-factor">
                <Box sx={styles.rowWithTooltip}>
                  <Typography variant="body2" sx={styles.heading}>
                    {t('Reduction Factor')}
                  </Typography>
                  <Tooltip
                    placement="top"
                    title={t('The percentage of total requests that your contextual segments are included in.')}
                  >
                    <InfoIcon
                      size={24}
                      color={theme.palette.text.secondary}
                      data-test="contextual-reduction-factor-info-icon"
                    />
                  </Tooltip>
                </Box>
                <Typography sx={styles.value} variant="h2">
                  {contextualReductionFactorLabel}
                </Typography>
              </Box>
              <Box sx={styles.column} data-test="contextual-impressions">
                <Box sx={styles.rowWithTooltip}>
                  <Typography variant="body2" sx={styles.heading}>
                    {t('Impressions')}
                  </Typography>
                  <Tooltip placement="top" title={t('Estimated impressions for the selected contextual segments.')}>
                    <InfoIcon
                      size={24}
                      color={theme.palette.text.secondary}
                      data-test="contextual-impressions-info-icon"
                    />
                  </Tooltip>
                </Box>
                <Typography sx={styles.value} variant="h2">
                  {contextualImpressionsLabel}
                </Typography>
              </Box>
            </Box>
          </Collapsible>
        </Box>
      )}
    </Box>
  );
};
